import { useQuery } from '@tanstack/react-query';
import { getSeedance25DraftAvailability, type Seedance25DraftAvailability } from '@/api/seedance25.api';
import { useWorkflowStore } from '@/state/workflow.state';

export const SEEDANCE_25_DRAFT_AVAILABILITY_QUERY_KEY_ROOT = ['seedance-25-draft-availability'] as const;

/**
 * Whether a draft the server has answered for can no longer be rendered in high res: it wasn't
 * found (older than seven days, or made in another workspace), or its expiry has since passed.
 * Unanswered (loading or failed) is not unavailable: the run itself still fails closed.
 */
export function isSeedance25DraftUnavailable(
  availability: Seedance25DraftAvailability | undefined,
  now: number = Date.now(),
): boolean {
  if (!availability) {
    return false;
  }
  if (!availability.available) {
    return true;
  }
  const expiresAt = availability.expiresAt ? Date.parse(availability.expiresAt) : NaN;
  return Number.isFinite(expiresAt) && expiresAt <= now;
}

/**
 * Asks the server whether Generate high res can render a draft result, before the user clicks.
 * Sends the open workflow: a run is recorded in the workflow's workspace, so that is where the
 * server looks the draft up (the user's active workspace can differ).
 */
export function useSeedance25DraftAvailability(asset: { id?: string; url?: string } | undefined) {
  const workflowId = useWorkflowStore((state) => state.recipe?.id);
  const { data } = useQuery({
    queryKey: [...SEEDANCE_25_DRAFT_AVAILABILITY_QUERY_KEY_ROOT, workflowId, asset?.id, asset?.url],
    queryFn: () => getSeedance25DraftAvailability(workflowId, asset!),
    enabled: Boolean(workflowId && (asset?.id || asset?.url)),
    staleTime: 60_000,
    retry: 1,
  });
  return { isUnavailable: isSeedance25DraftUnavailable(data) };
}
